import React from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import axiosInstance from "../utility/axios";

type UploadResponse = {
  success: boolean;
  message: string;
  data: {
    id: number;
    file_path: string;
    file_url: string;
  };
};

export type UploadedFile = {
  id: number;
  path: string;
  file_path: string;
  file_url: string;
  base_url: string;
};

const useUploadFiles = () => {
  const [loader, loaderSet] = React.useState(false);
  const uploadIdsByPath = React.useRef(new Map<string, number>());

  const uploadFile = async (
    files: File | File[] | null,
    module = "onboarding",
    requestedType?: "image" | "document",
  ): Promise<UploadedFile[]> => {
    if (!files) return [];
    loaderSet(true);

    try {
      const selectedFiles = Array.isArray(files) ? files : [files];
      const uploaded = await Promise.all(
        selectedFiles.map(async (file) => {
          const type =
            requestedType ??
            (file.type.startsWith("image/") ? "image" : "document");
          const formData = new FormData();
          formData.append("type", type);
          formData.append("module", module);
          formData.append("file", file);

          const response = await axiosInstance.post<UploadResponse>(
            "/api/v1/admin/uploads",
            formData,
          );
          if (!response.data.success) {
            throw new Error(response.data.message || "Failed to upload file");
          }

          const { id, file_path, file_url } = response.data.data;
          if (!id || !file_path || !file_url) {
            throw new Error("Upload response is missing file details");
          }
          uploadIdsByPath.current.set(file_path, id);
          const baseUrl = file_url.endsWith(file_path)
            ? file_url.slice(0, -(file_path.length + 1))
            : file_url;

          return {
            id,
            path: file_path,
            file_path,
            file_url,
            base_url: baseUrl,
          };
        }),
      );

      return uploaded;
    } catch (error) {
      const message = isAxiosError(error)
        ? error.response?.data?.message
        : error instanceof Error
          ? error.message
          : undefined;
      toast.error(message || "Failed to upload file");
      return [];
    } finally {
      loaderSet(false);
    }
  };

  const deleteFiles = async (fileIdOrPath: string | number) => {
    loaderSet(true);
    try {
      const fileId =
        typeof fileIdOrPath === "number"
          ? fileIdOrPath
          : (uploadIdsByPath.current.get(fileIdOrPath) ?? fileIdOrPath);
      const response = await axiosInstance.delete(
        `/api/v1/admin/uploads/${fileId}`,
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to delete file");
      }
      if (typeof fileIdOrPath === "string") {
        uploadIdsByPath.current.delete(fileIdOrPath);
      }
      toast.success(response.data.message || "File deleted successfully");
      return response.data;
    } catch (error) {
      const message = isAxiosError(error)
        ? error.response?.data?.message
        : error instanceof Error
          ? error.message
          : undefined;
      toast.error(message || "Failed to delete file");
      return false;
    } finally {
      loaderSet(false);
    }
  };

  return { loader, uploadFile, deleteFiles };
};

export default useUploadFiles;
